import React, {useState, useEffect, useLayoutEffect, useMemo} from 'react';
import {ChevronDown, ArrowLeft, ChevronUp, CornerUpLeft, File, Maximize2, MessageCircle, Minimize2, X, Search, Send, Bell} from 'lucide-react';
import './conversation2.css';
import '../Files.css';
import { ObjectId } from 'bson'
import axios from "axios";
import {toastr} from "react-redux-toastr";
import {sanitizeHtml} from "../../utils/sanitizeHtml";
import {isValidFilename} from "../../server/utils/isValidFilename";
import moment from "moment/moment";
import _ from "lodash";
import MessageInput from "./utils/quillTextInput"
import FilePreviewHandler from "./utils/filePreview";
import DateRangePicker from "./utils/dateRangePicker";
import UserPicker from "./utils/userPicker";
import PropTypes from "prop-types";
import ValidationUI from "../ValidationUI";
import UploadHint from "../UploadHint";
import { useUploadLimits, isTooLarge } from "../../utils/uploadLimits";
import kpLogo from '../../img/keenpoint-logo-circle-nobg.png'

const Conversation2 = ({
    objects,
    initialObjects,
    allIds,
    byId,
    currentUser,
    placeholder = "typeAMessage",
    maxHeight = "440px",
    minHeight = "150px",
    t,
    language,
    groupModel,
    accessId,
    push,
    update,
    remove,
    removeById,
    url,
    path
}) => {
    const [newMessage, setNewMessage] = useState('');
    const [replyMessage, setReplyMessage] = useState(null);
    const [filesToUpload, setFilesToUpload] = useState([]); // Track selected files
    const [messageDiscussion, setMessageDiscussion] = useState(null); // State for toggling views //showDiscussion setShowDiscussion
    const [highlightedMessageId, setHighlightedMessageId] = useState(null);
    const [lastViewedMessageId, setLastViewedMessageId] = useState(null);  // Store message ID instead of DOM reference
    const [isMinimized, setIsMinimized] = useState(false); // Minimize state
    const [isSearching, setIsSearching] = useState(false);
    const [isMessagesCollapsed, setIsMessagesCollapsed] = useState(false); //checked
    const [isDocumentsCollapsed, setIsDocumentsCollapsed] = useState(false);
    const [searchQuery, setSearchQuery] = useState({text: '', dateRange: { startDate: null, endDate: null }, sentBy: []}); // State for search input
    const [uploadState, setUploadState] = useState({});
    const fileLimits = useUploadLimits('file')
    const acceptedFormats = fileLimits ? fileLimits.formats.map(format => `.${format}`).join(',') : undefined
    const [fileUploadError, setFileUploadError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const isMainDiscussion = !messageDiscussion
    const initialSubDiscussionOfSelectedMessage = !messageDiscussion ? [] : objects.filter(msg => {
        return msg.parentMessage?.id === messageDiscussion.id
    })

    const subDiscussionOfSelectedMessage = isMainDiscussion ? [] : objects.filter(msg => msg.parentMessage?.id === messageDiscussion.id)

    // Memoize the first non-seen main message (evaluated only once)
    const firstNonSeenMainMessage = useMemo(() => {
        return initialObjects.filter(msg => !msg.parentMessage).find(msg => !msg.seenBy.includes(currentUser.id));
    }, []); // Empty dependency array ensures it's only evaluated once

    const firstNonSeenSubMessage = initialSubDiscussionOfSelectedMessage.find(msg => !msg.seenBy.includes(currentUser.id))
    const firstNonSeenMessage = isMainDiscussion ? firstNonSeenMainMessage : firstNonSeenSubMessage

    //complete
    // Helper function to find incomplete message
    const getIncompleteMessage = () => {
        return objects.find(msg => msg.status === 'incomplete')
    }

    const incompleteMessageToValidate = () => {
        return !!filesToUpload.length || !!objects.find(msg => msg.status === 'incomplete')
    }

    //incomplete
    // Handle message input change
    const handleMessageChange = (value) => {
        const incompleteMessage = getIncompleteMessage()

        setNewMessage(value)

        const isEmptyMessage = ['<p></p>', '<p><br></p>', ''].includes(value.trim()) && !filesToUpload.length

        if (isEmptyMessage) {
            // Delete incomplete message only if there are no files attached
            if (incompleteMessage) {
                removeById(incompleteMessage.id)
            }
        } else {
            // Create or update incomplete message
            if (!incompleteMessage) {
                // Create new incomplete message
                const OID = new ObjectId()
                const newIncompleteMessage = {
                    id: OID.toString(),
                    text: value,
                    sender: currentUser,
                    responseTo: replyMessage,
                    parentMessage: messageDiscussion,
                    seenBy: [currentUser.id],
                    files: filesToUpload,
                    timestamp: new Date().toISOString(),
                    from: 'user',
                    status: 'incomplete'
                }
                push(newIncompleteMessage)
            } else {
                // Update existing incomplete message
                update(incompleteMessage.id, {
                    text: value,
                    timestamp: new Date().toISOString()
                })
            }
        }
    }


    const confirmedMessages = objects.filter(msg => msg.status!=='incomplete')
    //when first entering the component, we scroll into the first non seen msg, else we scroll to the end of the conversation
    useLayoutEffect(() => {
        const isInitialState = isMainDiscussion ?
            objects.length === initialObjects.length
            : subDiscussionOfSelectedMessage.length === initialSubDiscussionOfSelectedMessage.length
        if (isInitialState && !!firstNonSeenMessage) {
            //try this if the scroll propagated outside of the convo component
            const containerName = isMainDiscussion ?  '.conversation-messages' : '.conversation-subMessages'
            const container = document.querySelector(containerName);
            const element = document.getElementById(`message-${firstNonSeenMessage.id}`);
            if (element && container) {
                const containerRect = container.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();

                // Calculate the offset to center the element
                const containerHeight = containerRect.height;
                const elementOffset = elementRect.top - containerRect.top; // Offset relative to the container
                const elementHeight = elementRect.height;

                const offset = elementOffset - (containerHeight / 2) + (elementHeight / 2);

                // Apply the scroll
                container.scrollTop += offset;
            }
        } else {
            //scroll to the end of convo
            const containerName = isMainDiscussion ?  '.conversation-messages' : '.conversation-subMessages'
            const container = document.querySelector(containerName);
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight, // Scroll to the bottom
                    behavior: isInitialState ? 'auto' : 'smooth',          // Enable smooth scrolling
                })
            }
        }

        //updating seen by
        objects.forEach(msg => {
            const notYetSeenByCurrentUser = !msg.seenBy.includes(currentUser.id)
            const mainOrSubDiscussionMessageCondition = isMainDiscussion ? !msg.parentMessage : msg.parentMessage?.id === messageDiscussion.id
            if(notYetSeenByCurrentUser && mainOrSubDiscussionMessageCondition){
                msg.seenBy.push(currentUser.id)
            }
        })
    }, [confirmedMessages.length, isMainDiscussion]);

    // Single effect to handle scrolling when returning from discussion
    useLayoutEffect(() => {
        if (!messageDiscussion && lastViewedMessageId) {
            const element = document.getElementById(`message-${lastViewedMessageId}`);
            if (element) {
                scrollToAMessage(lastViewedMessageId)
            }
        }
    }, [messageDiscussion]);

    //incomplete
    const handleSend = () => {
        const incompleteMessage = getIncompleteMessage()
        const isEmptyMessage = ['<p></p>', '<p><br></p>', ''].includes(newMessage.trim()) && !filesToUpload.length

        if (!isEmptyMessage) {
            if (incompleteMessage) {
                // Convert incomplete message to complete
                update(incompleteMessage.id, {
                    timestamp: new Date().toISOString()
                }, ['status']) // Remove status property
            }
            setNewMessage('');
            setReplyMessage(null);
            setFilesToUpload([])
        }
    };

    const handleFileUpload = async (event) => {
        let filesToUploadPrep = []
        const files = Array.from(event.target.files); // Get all selected files
        if (files.length === 0) return;

        // Allow to re-upload the same file in a row
        event.target.value = null

        setFileUploadError(null);

        for (const file of files) {
            if (await isTooLarge(file, 'file')) {
                const errorMessage = `${file.name} : ${t('tooLargeFile')}`
                toastr.error(errorMessage);
                setFileUploadError(errorMessage);
                continue
            }

            const data = new FormData();
            const fileName = file.name
            const fileType = file.type
            const fileSize = file.size
            const chunkSize = 15 * 1024 * 1024 // 15MB
            let fileChunk = null, chunkNb = 0

            for (let start = 0; start < fileSize; start += chunkSize, chunkNb++) {
                if (start + chunkSize > fileSize) {
                    fileChunk = file.slice(start, fileSize)
                }
                else {
                    fileChunk = file.slice(start, start + chunkSize)
                }
                data.append('fileChunks', fileChunk, fileName)
            }

            data.append('fileType', fileType)
            data.append('fileSize', fileSize)
            data.append('chunkNb', chunkNb)

            const options = {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadState(prev => ({ ...prev, [file.name]: percent }));
                },
            };
            try {
                const response = await axios.post(`/${url}`, data, options);
                const fileMessage = {
                    id: response.data.id,
                    name: response.data.filename,
                    originalName: file.name,
                    filename: file.name, //we'll keep only this one, the other two were made to handle a problem that we don't have anymore
                    size: fileSize
                };
                filesToUploadPrep.push(fileMessage)
                toastr.success(`${file.name} uploaded successfully`);
            } catch (err) {
                const data = _.get(err, 'response.data', {});
                console.error(err);
                const errorMessage = data.message ? t(data.message, data) : t('errorUploading');
                toastr.error(errorMessage);
                setFileUploadError(errorMessage);
            } finally {
                setUploadState(prev => { const s = { ...prev }; delete s[file.name]; return s; });
            }
        }
        setFilesToUpload((prevFiles) => [...prevFiles, ...filesToUploadPrep]); // Append the new file

        //incomplete
        // Update incomplete message with new files if it exists, otherwise create one
        const incompleteMessage = getIncompleteMessage()
        if (incompleteMessage) {
            update(incompleteMessage.id, {
                files: [...incompleteMessage.files, ...filesToUploadPrep]
            })
        } else if (filesToUploadPrep.length > 0) {
            const OID = new ObjectId()
            push({
                id: OID.toString(),
                text: '<p></p>',
                sender: currentUser,
                responseTo: replyMessage,
                parentMessage: messageDiscussion,
                seenBy: [currentUser.id],
                files: filesToUploadPrep,
                timestamp: new Date().toISOString(),
                from: 'user',
                status: 'incomplete'
            })
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsDragging(false);
        }
    };

    const handleFileDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        handleFileUpload({ target: { files: event.dataTransfer.files, value: null } });
    };

    const handlePaste = (event) => {
        const items = event.clipboardData?.items;
        if (!items) return;

        const pastedFiles = [];
        for (const item of items) {
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) pastedFiles.push(file);
            }
        }

        if (pastedFiles.length > 0) {
            event.preventDefault();
            handleFileUpload({ target: { files: pastedFiles, value: null } });
        }
    };

    const detachFile = (file) => {
        setFilesToUpload((prevFiles) => prevFiles.filter((fil) => fil.id !== file.id));

        //incomplete
        // Update incomplete message to remove the file
        const incompleteMessage = getIncompleteMessage()
        if (incompleteMessage) {
            const remainingFiles = incompleteMessage.files.filter(f => f.id !== file.id)
            const isEmptyMessage = ['<p></p>', '<p><br></p>', ''].includes(newMessage.trim())
            if (!remainingFiles.length && isEmptyMessage) {
                removeById(incompleteMessage.id)
            } else {
                update(incompleteMessage.id, { files: remainingFiles })
            }
        }
    }

    const handleReply = (replyMessage) => {
        setReplyMessage(replyMessage); // Set the message text being replied to

        //incomplete
        // Update incomplete message with new reply target
        const incompleteMessage = getIncompleteMessage()
        if (incompleteMessage) {
            update(incompleteMessage.id, {
                responseTo: replyMessage
            })
        }
    };

    const handleDeleteReply = () => {
        setReplyMessage(null);

        //incomplete
        // Update incomplete message with new reply target
        const incompleteMessage = getIncompleteMessage()
        if (incompleteMessage) {
            update(incompleteMessage.id, {
                responseTo: null
            })
        }
    };

    const handleDelete = (message) => {
        //we should delete respondTo and submessages also if they exist

        const repliesToThisMessage = objects.filter(msg => msg.responseTo?.id === message.id).map(msg => msg.id)
        const subMessagesOfThisMessage = objects.filter(msg => msg.parentMessage?.id === message.id).map(msg => msg.id)

        let messagesToDelete = [message.id]
        let indexesOfMessagesToDelete = [objects.findIndex((msg) => msg.id === message.id)]

        if (repliesToThisMessage.length > 0) {
            messagesToDelete = [...messagesToDelete, ...repliesToThisMessage]
            const indexesToAdd = repliesToThisMessage.map(id => objects.findIndex((msg) => msg.id === id))
            indexesOfMessagesToDelete = [...indexesOfMessagesToDelete, ...indexesToAdd]
        }
        if (subMessagesOfThisMessage.length > 0) {
            messagesToDelete = [...messagesToDelete, ...subMessagesOfThisMessage]
            const indexesToAdd = subMessagesOfThisMessage.map(id => objects.findIndex((msg) => msg.id === id))
            indexesOfMessagesToDelete = [...indexesOfMessagesToDelete, ...indexesToAdd]
        }

        const indexNotFoundError = indexesOfMessagesToDelete.includes(-1)
        // Sort indexes in descending order to avoid shifting issues
        const sortedIndexes = _.orderBy(indexesOfMessagesToDelete, [], ['desc']);

        if (!indexNotFoundError) {
            sortedIndexes.forEach(index => {remove(index)})
        }
    };

    const handleStartDiscussion = (msg) => {
        setMessageDiscussion(msg)
        setReplyMessage(null);
        // Save current scroll position
        setLastViewedMessageId(msg.id);  // Store the ID of the message

        const incompleteMessage = getIncompleteMessage()
        if (incompleteMessage) {
            update(incompleteMessage.id, {parentMessage: msg})
        }
    }

    const handleRenameFile = (fileId, newName, messageId) => {
        if (!isValidFilename(newName)) {
            toastr.error(t('invalidFileName'));
            return;
        }
        if (messageId === 'incomplete') {
            // Renaming a file in the "filesToUpload" list
            setFilesToUpload(prev => prev.map(f => f.id === fileId ? { ...f, filename: newName } : f));

            // Also update the incomplete message if it exists
            const incompleteMessage = getIncompleteMessage();
            if (incompleteMessage) {
                const updatedFiles = incompleteMessage.files.map(f => f.id === fileId ? { ...f, filename: newName } : f);
                update(incompleteMessage.id, { files: updatedFiles });
            }
        } else if (messageId) {
            // Renaming a file in an already sent (confirmed) message
            const message = objects.find(msg => msg.id === messageId);
            if (message) {
                const updatedFiles = message.files.map(f => f.id === fileId ? { ...f, filename: newName } : f);
                update(messageId, { files: updatedFiles });
            }
        }
    };

    const handleBackFromDiscussion = () => {
        setMessageDiscussion(null);
        setReplyMessage(null);

        const incompleteMessage = getIncompleteMessage()
        if (incompleteMessage) {
            update(incompleteMessage.id, {parentMessage: null})
        }
    }

    const toggleMinimize = () => {
        setIsSearching(false);
        setIsMinimized(!isMinimized);
    };

    const toggleIsSearching = () => {
        setIsSearching(!isSearching);
    };

    const toggleMessages = () => {
        setIsMessagesCollapsed(!isMessagesCollapsed);
    };

    const toggleDocuments = () => {
        setIsDocumentsCollapsed(!isDocumentsCollapsed);
    };

    // Single effect to handle scrolling when returning from a sub-discussion
    const scrollToAMessage = (msgID) => {
        if (msgID) {
            const element = document.getElementById(`message-${msgID}`);
            if (element) {
                // Highlight the message
                setHighlightedMessageId(msgID);

                element.scrollIntoView({
                    behavior: 'auto',
                    block: 'nearest', // Align to the center of the view for better visibility
                });

                setTimeout(() => {
                    setHighlightedMessageId(null);
                }, 500);
            }
        }
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatFileSize = (sizeInBytes) => {
        if (sizeInBytes >= 1024 * 1024 * 1024) {
            return (sizeInBytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
        } else if (sizeInBytes >= 1024 * 1024) {
            return (sizeInBytes / (1024 * 1024)).toFixed(2) + ' MB';
        } else if (sizeInBytes >= 1024) {
            return (sizeInBytes / 1024).toFixed(2) + ' KB';
        } else {
            return sizeInBytes + ' B';
        }
    };

    const mainMessagesToDisplay = objects.filter(msg => !msg.parentMessage && msg.status!=='incomplete')

    const mainMessagesGroupedByDay = _.groupBy(mainMessagesToDisplay, (message) => {
        const date = new Date(message.timestamp);
        const formattedDate = new Intl.DateTimeFormat('fr-FR', {
            weekday: 'short', // Abbreviated weekday (e.g., lun)
            day: '2-digit',   // Day in two digits
            month: '2-digit', // Month in two digits
            year: 'numeric'   // Full year
        }).format(date);

        // Capitalize the first letter
        return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    });

    const subMessagesOfAMainMessage = !messageDiscussion ? [] : objects.filter(msg => (msg.parentMessage?.id === messageDiscussion.id || msg.id === messageDiscussion.id) && msg.status!=='incomplete')
    const subMessagesGroupedByDay = _.groupBy(subMessagesOfAMainMessage, (message) => {
        const date = new Date(message.timestamp);
        const formattedDate = new Intl.DateTimeFormat('fr-FR', {
            weekday: 'short', // Abbreviated weekday (e.g., lun)
            day: '2-digit',   // Day in two digits
            month: '2-digit', // Month in two digits
            year: 'numeric'   // Full year
        }).format(date);

        // Capitalize the first letter
        return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    });

    const isTimestampInRange = (timestamp, startDate, endDate) => {
        // Si l'une des dates est null, impossible de faire la vérification
        if (!startDate || !endDate) return false;

        // Convertir le timestamp ISO en objet Date
        const date = new Date(timestamp);

        // Réinitialiser les heures/minutes/secondes pour comparer seulement les dates
        const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const normalizedStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const normalizedEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

        // Vérifier si la date est comprise entre la date de début et la date de fin (incluses)
        return normalizedDate >= normalizedStart && normalizedDate <= normalizedEnd;
    };

    let searchedMessages = []
    let searchedDocuments = objects.map(msg => msg.files.map(file => ({...file, timestamp: msg.timestamp, sender: msg.sender, msgIdToScrollTo: msg.parentMessage?.id || msg.id, msgId: msg.id}))).flat()

    const isDoingTextSearch = searchQuery.text.length > 0
    const isDoingDateSearch = searchQuery.dateRange.startDate && searchQuery.dateRange.endDate
    const isDoingSentBySearch = searchQuery.sentBy.length > 0
    const isDoingASearch = isDoingTextSearch || isDoingDateSearch || isDoingSentBySearch

    if (isDoingASearch){
        searchedMessages = objects //initialization
        if (isDoingTextSearch) {
            searchedMessages = searchedMessages.filter( msg => msg.text.toLowerCase().includes(searchQuery.text.toLowerCase()))
            searchedDocuments = searchedDocuments.filter(file => file.filename.toLowerCase().includes(searchQuery.text.toLowerCase()))
        }
        if (isDoingDateSearch) {
            searchedMessages = searchedMessages.filter(msg => isTimestampInRange(msg.timestamp, searchQuery.dateRange.startDate, searchQuery.dateRange.endDate))
            searchedDocuments = searchedDocuments.filter(file => isTimestampInRange(file.timestamp, searchQuery.dateRange.startDate, searchQuery.dateRange.endDate))
        }
        if (isDoingSentBySearch) {
            const searchedUsers = searchQuery.sentBy.map(user => user.id)
            const isSearchingForSystemMessages = searchedUsers.includes('system')
            searchedMessages = searchedMessages.filter(msg => {
                const isSystemMessage = msg.from === 'system'
                const isInSearchedUsers = searchedUsers.includes(msg.sender?.id)
                return isSearchingForSystemMessages ? (isSystemMessage || isInSearchedUsers) : isInSearchedUsers
            })
            searchedDocuments = searchedDocuments.filter(file => searchQuery.sentBy.map(user => user.id).includes(file.sender.id))
        }
    }

    const systemFakeUserObject = {id :'system', name: 'Plateforme', avatar: kpLogo}
    const senders = objects.filter(msg => msg.from === 'user').map(msg => msg.sender).flat()
    const uniqUsersData = _.uniqBy(senders, 'id')
    let usersData = [systemFakeUserObject, ...uniqUsersData]

    return (
        <div>
            <div className="conversation-container">
                <label className="conversationComponent-label" >{t(path)}</label>
                {!messageDiscussion ? (
                    <>
                        <div className="searchBar">
                            <div className="searchBarButtons">
                                {
                                    firstNonSeenMainMessage &&
                                    <div className="notification-icon">
                                        <Bell className="notificationsButton" size={18}/>
                                        <span className="notification-dot"></span>
                                    </div>
                                }
                                <button className="searchButton" title={'Rechercher'}>
                                    <Search size={18} onClick={toggleIsSearching}></Search>
                                </button>
                                {/*<button className="minimizeButton" title={isMinimized ? "Déplier" : "Replier"} onClick={toggleMinimize}>
                                    {isMinimized ?
                                        <Maximize2 size={18} style={{flexShrink: 0}}/> :
                                        <Minimize2 size={18} style={{flexShrink: 0}}/>
                                    }
                                </button>*/}
                            </div>
                        </div>
                        <div className="container2">
                            <div className="container2-1">
                                <div
                                    className="conversation-messages"
                                    style={{
                                        maxHeight: maxHeight,
                                        height: isMinimized ? minHeight : maxHeight,
                                        transition: 'height 0.5s ease', // Smooth height transition
                                        willChange: 'height', // Add this
                                    }}
                                >
                                    {
                                        Object.keys(mainMessagesGroupedByDay).map((date) => (
                                            <div key={date} className="grouped-messages">
                                                {/* Date Header */}
                                                <div className="message-date">{date}</div>
                                                {/* Messages */}
                                                {mainMessagesGroupedByDay[date].map((message) => (
                                                    <>
                                                        {firstNonSeenMainMessage?.id === message.id && (
                                                            <div className="nonSeenMessagesBadge">
                                                            <span
                                                                style={{display: "inlineBlock"}}>Nouveaux messages</span>
                                                            </div>)}
                                                        <div
                                                            key={message.id}
                                                            id={`message-${message.id}`}
                                                            className={`message-container 
                                                                ${message.from !== 'user' 
                                                                ? message.sender?.id === currentUser.id 
                                                                    ? 'systemSent' 
                                                                    : 'systemReceived' 
                                                                : message.sender?.id === currentUser.id 
                                                                    ? 'sent' 
                                                                    : 'received'} 
                                                                ${highlightedMessageId === message.id ? 'highlighted' : ''
                                                            }`}
                                                        >
                                                            <div
                                                                className={`message-bubble 
                                                                    ${message.from !== 'user' ? 'system' : message.sender?.id === currentUser.id ? 'sent' : 'received'}
                                                                `}
                                                            >
                                                                <div className="message-header">
                                                                    <span className="sender-name">
                                                                        {message.from !== 'user' && <img src={kpLogo} className="system-sender-logo" alt="" />}
                                                                        {message.sender?.name}
                                                                    </span>
                                                                    {message.from === 'user' && (
                                                                        <div className='buttons'>
                                                                            <div className="openDiscussionWrapper">
                                                                                <button className="openDiscussionButton" title={'Ouvrir la discussion'}>
                                                                                    <MessageCircle size={18} onClick={() => handleStartDiscussion(message)}/>
                                                                                    {
                                                                                        /*if there is submessages */
                                                                                        (!!objects.filter(msg => msg.parentMessage?.id === message.id).length)
                                                                                        && (
                                                                                        <span
                                                                                            /*if there is non seen sub messages*/
                                                                                            className={
                                                                                                `discussionDot 
                                                                                                ${!!initialObjects.filter(msg => msg.parentMessage?.id === message.id && !msg.seenBy.includes(currentUser.id)).length ? 'new' : 'seen'}
                                                                                                ${message.sender?.id === currentUser.id ? 'sent' : 'received'}
                                                                                                `}
                                                                                        />
                                                                                    )}
                                                                                </button>
                                                                            </div>
                                                                            <button className="replyButton" title={'Répondre'}><CornerUpLeft size={18} onClick={() => handleReply(message)}/></button>
                                                                            {!initialObjects.some(msg => msg.id === message.id) &&
                                                                                <button className="deleteButton" title={'Supprimer'}><X size={18} onClick={() => handleDelete(message)}/></button>
                                                                            }
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {message.responseTo && (
                                                                    <div
                                                                        className={`replyMessage-header ${message.sender?.id === currentUser.id ? 'sent' : 'received'}`}
                                                                        onClick={() => scrollToAMessage(message.responseTo.id)}
                                                                    >
                                                                        <span className="reply-sender-name">{message.responseTo.sender.name}</span>
                                                                        {/*<p className="message-text">{message.responseTo.text}</p>*/}
                                                                        <div className="message-text" dangerouslySetInnerHTML={{__html: sanitizeHtml(message.responseTo.text)}}/>
                                                                    </div>
                                                                )}
                                                                {message.from !== 'user'
                                                                    ? <div className="message-text">{t(message.text)}</div>
                                                                    : <div className="message-text" dangerouslySetInnerHTML={{__html: sanitizeHtml(message.text)}}/>
                                                                }
                                                                {!!message.files.length && (
                                                                    <div className="messageFilesWrapper">
                                                                        {message.files.map((file) => (
                                                                            <FilePreviewHandler
                                                                                key={file.id}
                                                                                file={file}
                                                                                files={message.files}
                                                                                formatFileSize={formatFileSize}
                                                                                language={language}
                                                                                groupModel={groupModel} accessId={accessId}
                                                                                editable={message.sender?.id === currentUser.id}
                                                                                onRename={(fileId, newName) => handleRenameFile(fileId, newName, message.id)}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                <span className="message-time">{formatTime(message.timestamp)}</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                ))}
                                            </div>
                                        ))
                                    }
                                </div>
                                {/* Reply Preview Above Input */}
                                <div
                                    className={`message-input-container${isDragging ? ' drag-over' : ''}`}
                                    onDragOver={handleDragOver}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleFileDrop}
                                    onPaste={handlePaste}
                                >
                                    {!!filesToUpload.length && (
                                        <div className="filesUploadPreview">
                                            <File className="FileIcon" size={18}
                                                  style={{cursor: "default", flexShrink: 0}}/>
                                            <div className="file-items-wrapper">
                                                {filesToUpload.map((file, index) => (
                                                    <FilePreviewHandler
                                                        key={file.id}
                                                        file={file}
                                                        files={filesToUpload}
                                                        formatFileSize={formatFileSize}
                                                        language={language}
                                                        groupModel={groupModel} accessId={accessId}
                                                        editable={true}
                                                        onRename={(fileId, newName) => handleRenameFile(fileId, newName, 'incomplete')}
                                                    />
                                                    /*<div key={index} className="file-item">
                                                        <span className="file-name">{file.originalName}</span>
                                                        <span className="file-size">{formatFileSize(file.size)}</span>
                                                        <X className="deleteFileIcon" size={18} style={{flexShrink: 0}}
                                                           onClick={() => detachFile(file)}/>
                                                    </div>*/
                                                ))}
                                            </div>
                                            {/* Clear reply on click */}
                                        </div>
                                    )}
                                    {Object.entries(uploadState).map(([filename, percent]) => (
                                        <div key={filename} className="conversation-upload-progress">
                                            <div className="conversation-upload-progress-info">
                                                <span>{filename}</span>
                                                <span>{percent === 100 ? t('processingFile') : `${percent}%`}</span>
                                            </div>
                                            <div className="conversation-progress-bar-container">
                                                <div className={percent === 100 ? 'conversation-progress-bar-fill upload-processing' : 'conversation-progress-bar-fill'} style={{ width: `${percent}%` }} />
                                            </div>
                                        </div>
                                    ))}
                                    {replyMessage && (
                                        <div className="reply-preview">
                                            <div className={`replyMessage-header sent`}>
                                                <span className="reply-sender-name">{replyMessage.sender?.name}</span>
                                                <div className="message-text" dangerouslySetInnerHTML={{__html: sanitizeHtml(replyMessage.text)}}/>
                                            </div>
                                            <button className="cancelReplyButton" onClick={() => handleDeleteReply()}>
                                                <X size={18}/>
                                            </button>
                                            {/* Clear reply on click */}
                                        </div>
                                    )}
                                    <div
                                         className='messageInputContainer'
                                    >
                                        <MessageInput
                                            className='textInput'
                                            value={newMessage}
                                            onChange={handleMessageChange} //handleMessageChange setNewMessage //incomplete
                                            placeholder={t(placeholder)}
                                            required={false}
                                            path=""
                                            t={t}
                                            readOnly={false}
                                            disabled={false}
                                            onFileSelect={handleFileUpload}  // Pass the file upload handler
                                            accept={acceptedFormats}
                                            attachmentHint={<UploadHint kind="file" t={t} />}
                                        />
                                        <button
                                            className={
                                                !getIncompleteMessage() ?  'send-button' : 'send-button Message-focus'
                                            }
                                            onClick={handleSend}>
                                            <Send size={20}/>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {
                                isSearching &&
                                <div className="container2-2">
                                    <div className="container2-2-1">
                                        <span className="titleSpan">Rechercher des messages</span>
                                        <X className="xButton" size={20} onClick={() => setIsSearching(false)}></X>
                                    </div>
                                    <div className="container2-2-2">
                                        <UserPicker users={usersData} preselectedUsers={searchQuery.sentBy} onChange={(users) => setSearchQuery({ ...searchQuery, sentBy: users })}/>
                                        <DateRangePicker preSelectedDateRange={searchQuery.dateRange} onChange={(range) => setSearchQuery({ ...searchQuery, dateRange: range })}/>
                                        <input
                                            type="text"
                                            placeholder="Rechercher..."
                                            value={searchQuery.text}
                                            onChange={(e) => setSearchQuery({ ...searchQuery, text: e.target.value })}
                                        />
                                    </div>
                                    <div className="container2-2-3">
                                        <div className="container2-2-3-1">
                                            <div>
                                                <span className="titleSpan">Messages</span>
                                                {searchedMessages.length > 0 ? <span className="searchCountBadge">{searchedMessages.length}</span> : null}
                                            </div>
                                            <button className="minusButton" title={isMessagesCollapsed ? 'Déplier' : 'Replier' } onClick={toggleMessages}>
                                                {isMessagesCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20}/>}
                                            </button>
                                        </div>
                                        {!isMessagesCollapsed && (searchedMessages.length > 0 ?
                                            <div className={`container2-2-3-2-nonEmpty`}>
                                                {searchedMessages.map((message) => (
                                                    <div
                                                        key={'search'+message.id}
                                                        id={`searchMessage-${message.id}`}
                                                        className="searchedMessage"
                                                        onClick={() => scrollToAMessage( message.parentMessage?.id || message.id)}
                                                    >
                                                        <div className="searched-message-header">
                                                            <span className="sender-name">{message.sender?.name}</span>
                                                            <span className="searched-message-date">{moment(message.timestamp).format("DD/MM/YYYY")}</span>
                                                        </div>
                                                        <div className="message-text" dangerouslySetInnerHTML={{__html: sanitizeHtml(message.text)}}/>
                                                    </div>
                                                ))}
                                            </div>
                                            : <div className="container2-2-3-2-Empty"><p className="no-results">Aucun message trouvé</p></div>
                                        )}
                                        <div className="container2-2-3-3">
                                            <div>
                                                <span className="titleSpan">Documents</span>
                                                {searchedDocuments.length > 0 ? <span className="searchCountBadge">{searchedDocuments.length}</span> : null}
                                            </div>
                                            <button className="minusButton"
                                                    title={isDocumentsCollapsed ? 'Déplier' : 'Replier'}
                                                    onClick={toggleDocuments}>
                                                {isDocumentsCollapsed ? <ChevronDown size={20}/> : <ChevronUp size={20}/>}
                                            </button>
                                        </div>
                                        {!isDocumentsCollapsed && (searchedDocuments.length > 0 ?
                                            <div className={`container2-2-3-4-nonEmpty`}>
                                                <div className="searchedFilesWrapper">
                                                    {searchedDocuments.map((file) => (
                                                        <FilePreviewHandler
                                                            className="searchedDocument"
                                                            key={"search" + file.id}
                                                            file={file}
                                                            files={searchedDocuments}
                                                            formatFileSize={formatFileSize}
                                                            clickableName={true}
                                                            onClickOnName={() => scrollToAMessage(file.msgIdToScrollTo)}
                                                            language={language}
                                                            groupModel={groupModel} accessId={accessId}
                                                            editable={file.sender.id === currentUser.id}
                                                            onRename={(fileId, newName) => handleRenameFile(fileId, newName, file.msgId)}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                            : <div className='container2-2-3-4-Empty'><p className="no-results">Aucun document trouvé</p></div>
                                        )}
                                    </div>
                                </div>
                            }
                        </div>
                    </>
                ) : (
                    /* New Discussion View */
                    <>
                        <div className="searchBar">
                            <button className={`back-button expanding`} onClick={() => handleBackFromDiscussion()}>
                                <ArrowLeft className="icon-left" size={18} style={{flexShrink: 0, marginLeft: "-4px", color: '#6b7280'}}/>
                            </button>
                            <div className="searchBarButtons">
                                {/*<button className="minimizeButton" title={isMinimized ? "Déplier" : "Replier"}
                                        onClick={toggleMinimize}>
                                    {isMinimized ?
                                        <Maximize2 size={18} style={{flexShrink: 0}}/> :
                                        <Minimize2 size={18} style={{flexShrink: 0}}/>
                                    }
                                </button>*/}
                            </div>
                        </div>
                        <div className="container2-1">
                            <div className="conversation-subMessages"
                                 style={{
                                     maxHeight: maxHeight,
                                     height: isMinimized ? minHeight : maxHeight,
                                     transition: 'height 0.1s ease', // Smooth height transition
                                     willChange: 'height', // Add this
                                 }}
                            >
                                {
                                    Object.keys(subMessagesGroupedByDay).map((date) => (
                                        <div key={date} className="grouped-messages">
                                            {/* Date Header */}
                                            <div className="message-date"><span>{date}</span></div>
                                            {/* Messages */}
                                            {subMessagesGroupedByDay[date].map((message) => (
                                                <>
                                                    {firstNonSeenSubMessage?.id === message.id && (
                                                        <div className="nonSeenMessagesBadge">
                                                            <span style={{display: "inlineBlock"}}>Nouveaux messages</span>
                                                        </div>)}
                                                    <div
                                                        key={message.id}
                                                        id={`message-${message.id}`}
                                                        className={`message-container ${message.sender?.id === currentUser.id ? 'sent' : 'received'}`}
                                                    >
                                                        <div className={`message-bubble ${message.sender?.id === currentUser.id ? 'sent' : 'received'}`}>
                                                            <div className="message-header">
                                                                <span className="sender-name">{message.sender?.name}</span>
                                                                <div className='buttons'>
                                                                    <button className="replyButton" title={'Répondre'}>
                                                                        <CornerUpLeft size={18} onClick={() => handleReply(message)}/>
                                                                    </button>
                                                                    {!initialObjects.some(msg => msg.id === message.id) &&
                                                                        <button className="deleteButton" title={'Supprimer'}>
                                                                            <X size={18} onClick={() => handleDelete(message)}/>
                                                                        </button>
                                                                    }
                                                                </div>
                                                            </div>
                                                            {message.responseTo && (
                                                                <div className={`replyMessage-header ${message.sender?.id === currentUser.id ? 'sent' : 'received'}`}>
                                                                    <span className="reply-sender-name"> {message.responseTo.sender.name} </span>
                                                                    <div className="message-text" dangerouslySetInnerHTML={{__html: sanitizeHtml(message.responseTo.text)}}/>
                                                                </div>
                                                            )}
                                                            {message.from !== 'user'
                                                                ? <div className="message-text">{t(message.text)}</div>
                                                                : <div className="message-text" dangerouslySetInnerHTML={{__html: sanitizeHtml(message.text)}}/>
                                                            }
                                                            {!!message.files.length && (
                                                                <div className="messageFilesWrapper">
                                                                    {message.files.map((file) => (
                                                                        <FilePreviewHandler
                                                                            key={file.id}
                                                                            file={file}
                                                                            files={message.files}
                                                                            formatFileSize={formatFileSize}
                                                                            language={language}
                                                                            groupModel={groupModel} accessId={accessId}
                                                                            editable={message.sender?.id === currentUser.id}
                                                                            onRename={(fileId, newName) => handleRenameFile(fileId, newName, message.id)}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                            <span className="message-time">{formatTime(message.timestamp)}</span>
                                                        </div>
                                                    </div>
                                                </>
                                            ))}
                                        </div>
                                    ))
                                }
                            </div>
                            {/* Reply Preview Above Input */}
                            <div
                                className={`message-input-container${isDragging ? ' drag-over' : ''}`}
                                onDragOver={handleDragOver}
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                                onDrop={handleFileDrop}
                                onPaste={handlePaste}
                            >
                                {!!filesToUpload.length && (
                                    <div className="filesUploadPreview">
                                        <File className="FileIcon" size={18} style={{cursor: "default", flexShrink: 0}}/>
                                        <div className="file-items-wrapper">
                                            {filesToUpload.map((file, index) => (
                                                <FilePreviewHandler
                                                    key={file.id}
                                                    file={file}
                                                    files={filesToUpload}
                                                    formatFileSize={formatFileSize}
                                                    language={language}
                                                    groupModel={groupModel} accessId={accessId}
                                                    editable={true}
                                                    onRename={(fileId, newName) => handleRenameFile(fileId, newName, 'incomplete')}
                                                />
                                            ))}
                                            {/*<div key={index} className="file-item">
                                                    <span className="file-name">{file.originalName}</span>
                                                    <span className="file-size">{formatFileSize(file.size)}</span>
                                                    <X className="deleteFileIcon" size={18} style={{flexShrink: 0}} onClick={() => detachFile(file)}/>
                                                </div>*/}
                                        </div>
                                        {/* Clear reply on click */}
                                    </div>
                                )}
                                {Object.entries(uploadState).map(([filename, percent]) => (
                                    <div key={filename} className="conversation-upload-progress">
                                        <div className="conversation-upload-progress-info">
                                            <span>{filename}</span>
                                            <span>{percent === 100 ? t('processingFile') : `${percent}%`}</span>
                                        </div>
                                        <div className="conversation-progress-bar-container">
                                            <div className={percent === 100 ? 'conversation-progress-bar-fill upload-processing' : 'conversation-progress-bar-fill'} style={{ width: `${percent}%` }} />
                                        </div>
                                    </div>
                                ))}
                                {replyMessage && (
                                    <div className="reply-preview">
                                        <div className={`replyMessage-header sent`}>
                                            <span className="reply-sender-name">{replyMessage.sender?.name}</span>
                                            <div className="message-text" dangerouslySetInnerHTML={{__html: sanitizeHtml(replyMessage.text)}}/>
                                        </div>
                                        <button className="cancelReplyButton" onClick={() => handleDeleteReply()}>
                                            <X size={18}/>
                                        </button>
                                        {/* Clear reply on click */}
                                    </div>
                                )}
                                <div className="messageInputContainer">
                                    <MessageInput
                                        className='textInput'
                                        value={newMessage}
                                        onChange={handleMessageChange}
                                        placeholder={t(placeholder)}
                                        required={false}
                                        path=""
                                        t={t}
                                        readOnly={false}
                                        disabled={false}
                                        onFileSelect={handleFileUpload}  // Pass the file upload handler
                                        accept={acceptedFormats}
                                        attachmentHint={<UploadHint kind="file" t={t} />}
                                    />
                                    <button
                                        className={
                                        !getIncompleteMessage() ?  'send-button' : 'send-button Message-focus'
                                        }
                                        onClick={handleSend}>
                                        <Send size={20}/>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
            { incompleteMessageToValidate() && <ValidationUI error={t('messageValidation')}/> }
        </div>
    );
};

export default Conversation2;

Conversation2.propTypes = {
    push: PropTypes.func.isRequired,
    update: PropTypes.func.isRequired, // New prop for updating messages
    remove: PropTypes.func.isRequired, // New prop for removing messages
    removeById: PropTypes.func.isRequired, // New prop for removing messages by id
    t: PropTypes.func
}
